import React from 'react';
import { ModalShell } from './ModalShell';
import { Eye, X, Copy, Brain } from 'lucide-react';

interface PromptPreviewModalProps {
  previewPrompt: string;
  /** System instruction sent separately from the message (v2.1 prompt); shown read-only. */
  systemPrompt?: string;
  onClose: () => void;
  onGenerate: () => void;
  onCopy: () => void;
}

export const PromptPreviewModal: React.FC<PromptPreviewModalProps> = ({
  previewPrompt,
  systemPrompt,
  onClose,
  onGenerate,
  onCopy
}) => {
  return (
    <ModalShell labelledBy="prompt-preview-title" onEscape={onClose} panelClassName="max-w-[800px] max-h-[85vh] p-5 sm:p-8">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 id="prompt-preview-title" style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Eye style={{ width: '20px', height: '20px', color: 'var(--accent-strong)' }} />
            Podgląd prompta
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
              color: 'var(--error)',
              border: '1px solid color-mix(in srgb, var(--error) 35%, transparent)',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 24%, transparent)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 12%, transparent)')}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>

        <div style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'var(--surface-subtle)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '20px',
          fontSize: '13px',
          lineHeight: '1.6',
          fontFamily: 'monospace',
          marginBottom: '24px',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
        }}>
          {systemPrompt && (
            <>
              <div style={{ fontFamily: 'inherit', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Instrukcja systemowa (wysyłana osobno)
              </div>
              <pre data-testid="preview-system-prompt" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 16px', color: 'var(--text-secondary)' }}>
                {systemPrompt}
              </pre>
              <div style={{ fontFamily: 'inherit', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Wiadomość z danymi
              </div>
            </>
          )}
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, color: 'var(--text-secondary)' }}>
            {previewPrompt}
          </pre>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCopy}
            style={{
              padding: '10px 18px',
              backgroundColor: 'var(--surface-subtle)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--border)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-subtle)')}
          >
            <Copy style={{ width: '16px', height: '16px' }} />
            Kopiuj
          </button>

          <button
            onClick={onClose}
            style={{
              padding: '10px 18px',
              backgroundColor: 'var(--surface-card)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-subtle)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-card)')}
          >
            Anuluj
          </button>

          <button
            onClick={onGenerate}
            style={{
              padding: '10px 18px',
              backgroundColor: 'var(--accent-strong)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(4, 120, 87, 0.2)',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-strong)')}
          >
            <Brain style={{ width: '16px', height: '16px' }} />
            Wyślij do AI
          </button>
        </div>
    </ModalShell>
  );
};
